import React, { useState } from "react";
import { FaEdit } from "react-icons/fa";
import {
  ConfirmDialog,
  FormField,
  PrimaryButton,
  SecondaryButton,
  formatSquadInfo,
  formatSquadInfoShort,
} from "../../../components/UI/DesignStandards";

const DetailRow = ({ label, value }) => (
  <div>
    <div className="text-xs font-medium text-brand-muted mb-1">{label}</div>
    <div className="text-sm text-brand-charcoal">{value}</div>
  </div>
);

export const squadDisplayLabel = (squad) => {
  if (!squad) return "Not assigned";
  const when = formatSquadInfo(squad.squadDate, squad.squadTime);
  const house = squad.bowlingCenters?.centerName;
  return house ? `${when} • ${house}` : when;
};

export const squadOptionLabel = (squad) => {
  if (!squad) return "Not assigned";
  const when = formatSquadInfoShort(squad.squadDate, squad.squadTime);
  const house = squad.bowlingCenters?.centerName;
  return house ? `${when} • ${house}` : when;
};

export const remapTeamSidePots = (bowlers, allEvents, newSquadId) => {
  const droppedNames = new Set();
  const nextBowlers = (bowlers || []).map((bowler) => {
    const selected = bowler.selectedSidePots || {};
    const next = {};
    Object.keys(selected).forEach((eventId) => {
      if (!selected[eventId]) return;
      const event = (allEvents || []).find((row) => row.id === eventId);
      if (!event) return;
      if (!event.templateId) {
        next[eventId] = true;
        return;
      }
      if (event.squadId === newSquadId) {
        next[eventId] = true;
        return;
      }
      const sibling = (allEvents || []).find(
        (row) =>
          row.templateId === event.templateId && row.squadId === newSquadId
      );
      if (sibling) {
        next[sibling.id] = true;
        return;
      }
      if (event.eventName) droppedNames.add(event.eventName);
    });
    return {
      ...bowler,
      selectedSidePots: next,
      paidEvents: Object.keys(next),
    };
  });
  return { bowlers: nextBowlers, droppedNames: Array.from(droppedNames) };
};

const squadMoveConfirmMessage = (squad, droppedNames) => {
  const list = droppedNames.join(", ");
  const verb = droppedNames.length === 1 ? "is" : "are";
  return `This team will bowl on ${squadDisplayLabel(
    squad
  )}. Starting lane will be cleared. ${list} ${verb} not on the new squad and will be removed.`;
};

const TeamSquadCard = ({
  squad,
  squads = [],
  droppedNamesForSquad,
  onSave,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState(false);
  const [saving, setSaving] = useState(false);

  const savedId = squad?.id || "";
  const pendingSquad = squads.find((row) => row.id === draft) || null;
  const droppedNames = pendingSquad
    ? droppedNamesForSquad(pendingSquad.id)
    : [];

  const handleStartEdit = () => {
    setDraft(savedId);
    setIsEditing(true);
  };

  const commit = async () => {
    setSaving(true);
    try {
      await onSave(draft);
      setPending(false);
      setIsEditing(false);
    } catch (error) {
      setPending(false);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = (e) => {
    e.preventDefault();
    if (!draft || draft === savedId) {
      setIsEditing(false);
      return;
    }
    if (droppedNames.length) {
      setPending(true);
      return;
    }
    commit();
  };

  const handleCancel = () => {
    setDraft(savedId);
    setPending(false);
    setIsEditing(false);
  };

  return (
    <div className="card-inset p-4 space-y-4 h-full">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="heading-display text-lg">Squad</h2>
        </div>
        {!isEditing ? (
          <SecondaryButton
            type="button"
            onClick={handleStartEdit}
            icon={FaEdit}
            className="!px-3 !py-2 text-sm flex-shrink-0"
          >
            Edit
          </SecondaryButton>
        ) : null}
      </div>

      {isEditing ? (
        <form onSubmit={handleSave} className="space-y-4">
          <FormField
            label="Squad"
            type="select"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          >
            {squads.map((row) => (
              <option key={row.id} value={row.id}>
                {squadOptionLabel(row)}
              </option>
            ))}
          </FormField>
          <div className="flex flex-wrap gap-2">
            <PrimaryButton type="submit" loading={saving}>
              Save
            </PrimaryButton>
            <SecondaryButton type="button" onClick={handleCancel} disabled={saving}>
              Cancel
            </SecondaryButton>
          </div>
        </form>
      ) : (
        <DetailRow label="Squad" value={squadDisplayLabel(squad)} />
      )}

      <ConfirmDialog
        isOpen={pending}
        onClose={() => setPending(false)}
        onConfirm={commit}
        title="Move squad"
        message={squadMoveConfirmMessage(pendingSquad, droppedNames)}
        confirmText="Move squad"
        cancelText="Cancel"
        variant="warning"
        loading={saving}
      />
    </div>
  );
};

export default TeamSquadCard;
